import { useTranslation } from 'react-i18next';
import { Columns3 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import useOrderColumnsStore, {
  selectHiddenColumns,
  selectShowAllColumns,
  selectToggleColumn,
} from '@/stores/useOrderColumnsStore';
import { FOCUS_RING } from './toolbarConfig';

export interface ColumnOption {
  key: string;
  label: string;
}

interface ColumnsButtonProps {
  /** The columns this tab can show, minus the ones that always stay (order, actions). */
  options: ColumnOption[];
}

/** Show or hide table columns (owner 11-02). All on by default; the menu stays open while ticking. */
const ColumnsButton = ({ options }: ColumnsButtonProps) => {
  const { t } = useTranslation();
  const hidden = useOrderColumnsStore(selectHiddenColumns);
  const toggle = useOrderColumnsStore(selectToggleColumn);
  const showAll = useOrderColumnsStore(selectShowAllColumns);
  const hiddenHere = options.filter((o) => hidden.includes(o.key)).length;
  const label = String(t('table_columns'));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          aria-label={hiddenHere ? `${label} (${hiddenHere})` : label}
          title={label}
          className={`relative inline-flex items-center justify-center h-8 w-8 rounded-lg border-0 bg-transparent text-sd-foreground hover:bg-sd-muted cursor-pointer shrink-0 ${hiddenHere ? 'bg-sd-muted' : ''} ${FOCUS_RING}`}
        >
          <Columns3 aria-hidden className='w-4 h-4' />
          {hiddenHere ? (
            <span
              aria-hidden
              className='absolute top-1 end-1 w-1.5 h-1.5 rounded-full bg-sd-foreground'
            />
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-56'>
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className='max-h-[min(60vh,360px)] overflow-y-auto'>
          {options.map((o) => (
            <DropdownMenuCheckboxItem
              key={o.key}
              checked={!hidden.includes(o.key)}
              onCheckedChange={() => toggle(o.key)}
              onSelect={(e) => e.preventDefault()}
            >
              {o.label}
            </DropdownMenuCheckboxItem>
          ))}
        </div>
        {hiddenHere ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={(e) => (e.preventDefault(), showAll())}>
              {String(t('columns_show_all'))}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ColumnsButton;
